import React from 'react';
import { NavLink, useLocation, useNavigate, useParams } from 'react-router-dom';
import {
  Alert,
  Box,
  Button,
  Card,
  CardContent,
  CircularProgress,
  Divider,
  Stack,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Typography,
} from '@mui/material';
import CheckCircleOutlineIcon from '@mui/icons-material/CheckCircleOutline';
import { useAuth } from '../../contexts/AuthContext';
import PortalPageHeader from '../../components/portal/PortalPageHeader';

function formatMoney(n) {
  return `$${Number(n || 0).toFixed(2)}`;
}

function formatDate(d) {
  if (!d) return '—';
  try {
    return new Date(d).toLocaleDateString();
  } catch {
    return '—';
  }
}

function lineItemDescription(line) {
  const title = String(line?.product_name || '').trim() || 'Item';
  const sub = String(line?.variation_name || '').trim();
  const qty = Math.max(1, parseInt(line?.quantity, 10) || 1);
  const unit = Number(line?.unit_price || 0);
  const lineTotal = line?.line_total != null ? Number(line.line_total) : unit * qty;
  const travel = Number(line?.out_of_town_surcharge || 0);
  const merch =
    line?.merchandise_total != null
      ? Number(line.merchandise_total)
      : lineTotal - (Number.isFinite(travel) ? travel : 0);
  let namePart = sub ? `${title} — ${sub}` : title;
  const days = parseInt(line?.out_of_town_days, 10);
  if (Number.isFinite(days) && days > 0) {
    namePart += ` (outside Shelby County, ${days} day${days === 1 ? '' : 's'})`;
  }
  return { namePart, qty, unit, lineTotal: merch };
}

function contributionForFromItems(items) {
  if (!Array.isArray(items) || items.length === 0) return 'Donation';
  return items
    .map((line) => {
      const { namePart, qty } = lineItemDescription(line);
      return qty > 1 ? `${namePart} ×${qty}` : namePart;
    })
    .join('; ');
}

/**
 * Mirrors `PortalPledgePaymentReceiptPage` layout (reference #, divider, same body field order).
 * Donation order # is shown only when it differs from the contribution reference (Woo) id.
 */
function DonateCatalogReceiptCard({ result, orderIdFallback }) {
  const wooOrderId = result?.wooOrderId ?? result?.orderId;
  const donationOrderId = result?.pledge?.id ?? orderIdFallback;
  const showDonationOrderRow =
    donationOrderId != null &&
    String(donationOrderId).trim() !== '' &&
    String(donationOrderId) !== String(wooOrderId ?? '');

  const items = Array.isArray(result?.items) ? result.items : [];

  const feeAmount = Number(result?.amounts?.fee || 0);
  const outOfTownAmount = Number(result?.amounts?.outOfTown || 0);
  const hasFeeLine = Number.isFinite(feeAmount) && feeAmount > 0.001;
  const hasOutOfTownLine = Number.isFinite(outOfTownAmount) && outOfTownAmount > 0.001;
  const normalizedItems =
    items.length > 0
      ? items
      : [
          {
            product_id: 'donation',
            variation_id: 0,
            product_name: result?.pledge?.productName || 'Donation',
            variation_name: result?.pledge?.variationName || '',
            quantity: 1,
            unit_price: Number(result?.amounts?.base || result?.amounts?.total || 0),
            line_total: Number(result?.amounts?.base || result?.amounts?.total || 0),
          },
        ];
  const subtotalFromItems = normalizedItems.reduce((sum, line) => {
    const { lineTotal } = lineItemDescription(line);
    return sum + Number(lineTotal || 0);
  }, 0);
  const subtotalAmount = Number.isFinite(Number(result?.amounts?.base))
    ? Number(result.amounts.base)
    : subtotalFromItems;
  const totalAmount = Number.isFinite(Number(result?.amounts?.total))
    ? Number(result.amounts.total)
    : subtotalAmount + (hasFeeLine ? feeAmount : 0);

  return (
    <Card
      variant="outlined"
      sx={{
        width: '100%',
        borderRadius: 2,
        '@media print': { boxShadow: 'none', borderColor: '#000' },
      }}
    >
      <CardContent>
        <Stack direction="row" justifyContent="space-between" alignItems="flex-start" sx={{ mb: 1 }}>
          <Box>
            <Typography variant="h6" sx={{ fontWeight: 800 }}>
              Contribution Receipt
            </Typography>
            <Typography variant="body2" color="text.secondary">
              Contribution reference #
            </Typography>
          </Box>
          <Button variant="outlined" size="small" onClick={() => window.print()} sx={{ displayPrint: 'none' }}>
            Print
          </Button>
        </Stack>
        <Typography variant="h6" sx={{ fontWeight: 800, mb: 0.75 }}>
          #{wooOrderId}
        </Typography>
        <Divider sx={{ my: 1.5 }} />

        <Typography variant="subtitle2" sx={{ fontWeight: 700, mb: 0.75 }}>
          Items
        </Typography>
        <TableContainer>
          <Table size="small" aria-label="Contribution items receipt table">
            <TableHead>
              <TableRow>
                <TableCell sx={{ fontWeight: 700 }}>Item</TableCell>
                <TableCell align="right" sx={{ fontWeight: 700 }}>
                  Qty
                </TableCell>
                <TableCell align="right" sx={{ fontWeight: 700 }}>
                  Unit
                </TableCell>
                <TableCell align="right" sx={{ fontWeight: 700 }}>
                  Line total
                </TableCell>
              </TableRow>
            </TableHead>
            <TableBody>
              {normalizedItems.map((line, idx) => {
                const { namePart, qty, unit, lineTotal } = lineItemDescription(line);
                return (
                  <TableRow key={`${line.product_id}-${line.variation_id || 0}-${idx}`}>
                    <TableCell>{namePart}</TableCell>
                    <TableCell align="right">{qty}</TableCell>
                    <TableCell align="right">{formatMoney(unit)}</TableCell>
                    <TableCell align="right">{formatMoney(lineTotal)}</TableCell>
                  </TableRow>
                );
              })}
              <TableRow>
                <TableCell colSpan={3} align="right" sx={{ fontWeight: 700 }}>
                  Subtotal
                </TableCell>
                <TableCell align="right" sx={{ fontWeight: 700 }}>
                  {formatMoney(subtotalAmount)}
                </TableCell>
              </TableRow>
              {hasOutOfTownLine ? (
                <TableRow>
                  <TableCell colSpan={3} align="right" sx={{ fontWeight: 700 }}>
                    Outside of Shelby County
                  </TableCell>
                  <TableCell align="right" sx={{ fontWeight: 700 }}>
                    {formatMoney(outOfTownAmount)}
                  </TableCell>
                </TableRow>
              ) : null}
              {hasFeeLine ? (
                <TableRow>
                  <TableCell colSpan={3} align="right" sx={{ fontWeight: 700 }}>
                    Processing fee
                  </TableCell>
                  <TableCell align="right" sx={{ fontWeight: 700 }}>
                    {formatMoney(feeAmount)}
                  </TableCell>
                </TableRow>
              ) : null}
              <TableRow>
                <TableCell colSpan={3} align="right" sx={{ fontWeight: 800 }}>
                  Total paid
                </TableCell>
                <TableCell align="right" sx={{ fontWeight: 800 }}>
                  {formatMoney(totalAmount)}
                </TableCell>
              </TableRow>
            </TableBody>
          </Table>
        </TableContainer>

        <Divider sx={{ my: 1.5 }} />
        <Typography variant="subtitle2" sx={{ fontWeight: 700, mb: 0.75 }}>
          Payment details
        </Typography>
        <Stack spacing={0.5}>
          <Typography variant="body2">
            <strong>Date of contribution:</strong> {formatDate(result?.contributionDate)}
          </Typography>
          {showDonationOrderRow ? (
            <Typography variant="body2">
              <strong>Donation order #:</strong> {donationOrderId}
            </Typography>
          ) : null}
          <Typography variant="body2">
            <strong>Payment method:</strong> {result?.paymentMethodTitle || 'Card / ACH'}
          </Typography>
          {result?.billingEmail ? (
            <Typography variant="body2">
              <strong>Receipt email:</strong> {result.billingEmail}
            </Typography>
          ) : null}
          {result?.idempotent ? (
            <Typography variant="body2" color="text.secondary">
              (This payment was already recorded.)
            </Typography>
          ) : null}
        </Stack>
      </CardContent>
    </Card>
  );
}

/** Map donate finalize `checkout` payload into pledge receipt `result` shape. */
function checkoutToPledgeReceiptShape(checkout, routeOrderId) {
  if (!checkout || typeof checkout !== 'object') return null;
  const oid = checkout.wooOrderId ?? checkout.orderId ?? routeOrderId;
  const items = Array.isArray(checkout.items) ? checkout.items : [];
  const first = items[0];
  let productName;
  let variationName;
  if (items.length === 0) {
    productName = 'Donation';
    variationName = null;
  } else if (items.length === 1) {
    productName = String(first?.product_name || 'Donation').trim() || 'Donation';
    const v = String(first?.variation_name || '').trim();
    variationName = v || null;
  } else {
    productName = contributionForFromItems(items);
    variationName = null;
  }

  return {
    ...checkout,
    wooOrderId: oid,
    amounts: checkout.amounts || { total: checkout.total },
    contributionDate: checkout.contributionDate || checkout.date || new Date().toISOString(),
    pledge: {
      ...(checkout.pledge || {}),
      id: Number(checkout.pledge?.id || oid || routeOrderId),
      productName,
      variationName: variationName || null,
    },
    paymentMethodTitle: checkout.paymentMethodTitle,
    paymentIntentId: checkout.paymentIntentId,
    idempotent: checkout.idempotent,
    billingEmail: checkout.billingEmail,
  };
}

export default function PublicDonateConfirmationPage() {
  const { orderId } = useParams();
  const location = useLocation();
  const navigate = useNavigate();
  const { staff, loading: authLoading } = useAuth();
  const checkout = location.state?.checkout || null;

  const portalSubject =
    staff?.portal && staff?.subject != null ? String(staff.subject).trim() : null;
  const isPortalLoggedIn = Boolean(portalSubject);

  const displayOrderId = orderId || checkout?.orderId || checkout?.wooOrderId || '';
  const result = checkoutToPledgeReceiptShape(
    checkout ? { ...checkout, orderId: checkout.orderId ?? displayOrderId } : null,
    displayOrderId
  );

  const receiptEmail =
    String(result?.billingEmail || '').trim() ||
    (isPortalLoggedIn ? String(staff?.email || '').trim() : '');

  if (authLoading) {
    return (
      <Box sx={{ py: 5, display: 'flex', justifyContent: 'center' }}>
        <CircularProgress />
      </Box>
    );
  }

  if (isPortalLoggedIn) {
    return (
      <>
        <PortalPageHeader title="Payment receipt" showPoshakaCluster={false} />
        <Box sx={{ maxWidth: 760, mx: 'auto', width: '100%', display: 'flex', flexDirection: 'column', alignItems: 'center' }}>
          {result ? (
            <>
              <Alert severity="success" sx={{ mb: 2, width: '100%' }}>
                Thank you for your contribution. Here are your contribution details.
                {receiptEmail ? ` An email receipt was sent to ${receiptEmail}.` : ''}
              </Alert>
              <DonateCatalogReceiptCard result={result} orderIdFallback={displayOrderId} />
            </>
          ) : (
            <Alert severity="info" sx={{ mb: 2 }}>
              Order <strong>#{displayOrderId || '—'}</strong> is confirmed. Open this page from checkout after payment to
              see full receipt details, or view your donations in the portal.
            </Alert>
          )}

          <Stack
            direction={{ xs: 'column', sm: 'row' }}
            spacing={1.5}
            sx={{ mt: 3, width: '100%', maxWidth: 760 }}
            justifyContent="center"
            alignItems="center"
          >
            <Button variant="contained" onClick={() => navigate(`/devotees/${portalSubject}/donations`)}>
              Back to Donations
            </Button>
            <Button variant="outlined" component={NavLink} to="/donate">
              Donate again
            </Button>
            <Button variant="outlined" onClick={() => navigate(`/devotees/${portalSubject}/donations`)}>
              My Donations
            </Button>
          </Stack>
        </Box>
      </>
    );
  }

  return (
    <Stack spacing={2} alignItems="center" sx={{ width: '100%' }}>
      <Typography variant="h5" sx={{ fontWeight: 700, width: '100%', maxWidth: 760 }}>
        Donation confirmation
      </Typography>
      {result ? (
        <>
          <Alert severity="success" sx={{ mb: 2, width: '100%', maxWidth: 760 }}>
            Thank you for your contribution. Here are your contribution details.
            {receiptEmail ? ` An email receipt was sent to ${receiptEmail}.` : ''}
          </Alert>
          <Box sx={{ maxWidth: 760, mx: 'auto', width: '100%' }}>
            <DonateCatalogReceiptCard result={result} orderIdFallback={displayOrderId} />
          </Box>
        </>
      ) : (
        <Card variant="outlined" sx={{ width: '100%', maxWidth: 760 }}>
          <CardContent>
            <Stack spacing={1.25} alignItems="flex-start">
              <Stack direction="row" spacing={1} alignItems="center">
                <CheckCircleOutlineIcon color="success" />
                <Typography variant="h6" sx={{ fontWeight: 700 }}>
                  Payment successful
                </Typography>
              </Stack>
              <Typography variant="body2" color="text.secondary">
                Thank you for your support. Your donation order has been recorded.
              </Typography>
              <Typography variant="body2">
                Order ID: <strong>{displayOrderId || '—'}</strong>
              </Typography>
            </Stack>
          </CardContent>
        </Card>
      )}

      {!checkout ? (
        <Alert severity="info" sx={{ width: '100%', maxWidth: 760 }}>
          You reached confirmation directly. Order details may be limited, but your donation may already be processed.
        </Alert>
      ) : null}

      <Stack direction="row" spacing={1} flexWrap="wrap" justifyContent="center" sx={{ width: '100%', maxWidth: 760 }}>
        <Button variant="outlined" component={NavLink} to="/donate">
          Donate again
        </Button>
        <Button component={NavLink} to="/login">
          Devotee sign in
        </Button>
      </Stack>
    </Stack>
  );
}
